import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminApplications.css';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';

const AdminApplications = () => {
  const navigate = useNavigate();
  const [applications, setApplications] = useState([]);
  const [selectedApplications, setSelectedApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchMode, setSearchMode] = useState(false);
  const [confirmDialog, setConfirmDialog] = useState({
    open: false,
    message: '',
    onConfirm: null,
    loading: false,
  });

  const fetchApplications = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-form-submissions?page=${page}`);
      setApplications(response.data.applications || []);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error fetching applications', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchSearchedApplications = async (page = 1) => {
    try {
      setLoading(true);
      const response = await privateAxios.get(`/private/admin-search-applications?query=${searchTerm.trim()}&page=${page}`);
      setApplications(response.data.applications || []);
      setTotalPages(response.data.totalPages || 1);
      setCurrentPage(page);
    } catch (error) {
      console.error('Error searching applications', error);
      setApplications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchApplications();
  }, []);

  const handleSearch = () => {
    if (!searchTerm.trim()) {
      setSearchMode(false);
      fetchApplications(1);
    } else {
      setSearchMode(true);
      fetchSearchedApplications(1);
    }
  };

  const handleCheckbox = (id) => {
    setSelectedApplications((prev) =>
      prev.includes(id) ? prev.filter((a) => a !== id) : [...prev, id]
    );
  };

  const deleteSelectedApplications = async () => {
    if (!selectedApplications.length) return;
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete ${selectedApplications.length} selected application(s)?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.post('/private/delete-selected-applications', { ids: selectedApplications });
          setSelectedApplications([]);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedApplications(currentPage) : fetchApplications(currentPage);
        } catch (error) {
          console.error('Error deleting applications', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleDeleteSingle = (id, title) => {
    setConfirmDialog({
      open: true,
      message: `Are you sure you want to delete the application titled "${title}"?`,
      onConfirm: async () => {
        try {
          setConfirmDialog((prev) => ({ ...prev, loading: true }));
          await privateAxios.delete(`/private/delete-application/${id}`);
          setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
          searchMode ? fetchSearchedApplications(currentPage) : fetchApplications(currentPage);
        } catch (error) {
          console.error('Error deleting application', error);
          setConfirmDialog((prev) => ({ ...prev, loading: false }));
        }
      },
      loading: false
    });
  };

  const handleEdit = (id) => navigate(`/admin-edit-application/${id}`);

  const handleClickApplication = (id, e) => {
    if (e.target.closest('button') || e.target.closest('input')) return;
    navigate(`/admin-application/${id}`);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      const nextPage = currentPage + 1;
      searchMode ? fetchSearchedApplications(nextPage) : fetchApplications(nextPage);
    }
  };

  const handlePreviousPage = () => {
    if (currentPage > 1) {
      const prevPage = currentPage - 1;
      searchMode ? fetchSearchedApplications(prevPage) : fetchApplications(prevPage);
    }
  };

  return (
    <div className="admin-applications">
      <div className="admin-applications-header">
        <div className="admin-applications-search-input">
          <input
            type="text"
            placeholder="Search applications..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <button className="admin-applications-search-btn" onClick={handleSearch}>
            Search
          </button>
        </div>
        <button
          className="admin-applications-delete-selected-btn"
          style={{ backgroundColor: selectedApplications.length ? '#008751' : '#ccf1e4' }}
          onClick={deleteSelectedApplications}
          disabled={!selectedApplications.length}
        >
          Delete Selected
        </button>
      </div>

      <div className="admin-applications-list">
        {loading ? (
          <div>Loading...</div>
        ) : applications.length ? (
          applications.map((a) => (
            <div className="admin-applications-card" key={a._id} onClick={(e) => handleClickApplication(a._id, e)}>
              <div className="admin-applications-checkbox-title">
                <input
                  type="checkbox"
                  checked={selectedApplications.includes(a._id)}
                  onChange={() => handleCheckbox(a._id)}
                />
                <div className="admin-applications-info">
                  <h4>
                    {a.form?.title
                      ? a.form.title.split(' ').length > 10
                        ? a.form.title.split(' ').slice(0, 10).join(' ') + '...'
                        : a.form.title
                      : 'Untitled Application'}
                  </h4>
                  <small>
                    By: {a.applicant?.firstname || ''} {a.applicant?.lastname || ''} |{' '}
                    Created: {moment(a.createdAt).format('MMMM DD, YYYY')}
                    {a.updatedAt && ` | Last Edited: ${new Date(a.updatedAt).toLocaleDateString()}`}
                  </small>
                </div>
              </div>
              <div className="admin-applications-actions">
                {/* <button className="admin-applications-edit-button" onClick={() => handleEdit(a._id)}>
                  <FaEdit />
                </button> */}
                <button className="admin-applications-delete-button" onClick={() => handleDeleteSingle(a._id, a.form?.title || 'Untitled')}>
                  <FaTrash />
                </button>
              </div>
            </div>
          ))
        ) : (
          <div>No applications found.</div>
        )}
      </div>

      <div className="admin-applications-pagination-controls">
        <button onClick={handlePreviousPage} disabled={currentPage === 1}>Previous</button>
        <span>Page {currentPage} of {totalPages}</span>
        <button onClick={handleNextPage} disabled={currentPage === totalPages}>Next</button>
      </div>

      <div className="admin-reports-back-to-admin-dashboard">
        <Link to={'/admin-dashboard'}>Back to Admin Dashboard</Link>
      </div>

      {confirmDialog.open && (
        <ConfirmDialog
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onCancel={() => setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })}
          loading={confirmDialog.loading}
        />
      )}
    </div>
  );
};

export default AdminApplications;